import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FiUploadCloud } from "react-icons/fi";
import { useEffect, useState } from 'react';
import { FaCircleInfo } from 'react-icons/fa6';
import { Link } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import IndustryAutocomplete from '../shared/components/FormAutoComplete';
import FileUploadField from '../shared/components/FileUploadField';

const handleGetValidationSchema = () => {
  return Yup.object().shape({
    State: Yup.string().required('State name is required'),
    year: Yup.string().required('Year is required').matches(/^\d{4}$/, 'Year must be a 4-digit number like 2025'),
    industryType: Yup.string()
      .required('Industry is required'),
    referenceLink: Yup.string().url('Enter a valid URL'),
    EffectiveFrom: Yup.string()
      .required('Effective From is required')
      .test('is-valid-date', 'Effective From must be a valid date', (value) => {
        return value && !isNaN(Date.parse(value));
      }),
    EffectiveTo: Yup.date(),
    NotifiedOn: Yup.date().test('is-valid-date', 'Effective From must be a valid date', (value) => {
      return !value || !isNaN(Date.parse(value));
    }),
    NotificationNo: Yup.string().required('Notification No is required'),
    fileUrl: Yup.string().required('File upload is required'),
  });
};

const UploadMinimumWagesForm = ({ industryTypes = [], states, onUploadMinimumWages, onUploadFile, data = null, onEdit, isEdit, selectedState = "", selectedIndustryType = "", onSetState }) => {
  const currentYear = new Date().getFullYear();
  const yearsOption = [currentYear - 1, currentYear, currentYear + 1];
  const [initialValues, setInitialValues] = useState({
    State: '',
    year: currentYear.toString(),
    industryType: '',
    NotificationNo: '',
    NotifiedOn: '',
    EffectiveFrom: '',
    EffectiveTo: '',
    referenceLink: '',
    fileUrl: '',
  });

  useEffect(() => {
    if (data) {
      setInitialValues({
        ...data,
        industryType: data.industryType || '',
        EffectiveTo: data.EffectiveTo === "N/A" ? "" : data.EffectiveTo,
        referenceLink: data.referenceLink === "N/A" ? '' : data.referenceLink,
        NotifiedOn: data.NotifiedOn === "N/A" ? "" : data.NotifiedOn,
        State: data.StateId,
      });
    }
  }, [data]);

  const handleOnUploadFile = async (file, stateId, industryId) => {
    return await onUploadFile(file, stateId, industryId);
  };

  return (
    <div className="w-full">
      <h2 className="text-xl font-bold text-primary-color text-center">Upload Minimum Wage File</h2>
      <Formik
        initialValues={{ ...initialValues }}
        validationSchema={handleGetValidationSchema}
        validateOnChange={true}
        validateOnMount={true}
        enableReinitialize={true}
        onSubmit={(values) => {
          const selectedState = states.find(s => s.StateId === values.State);
          const formattedData = {
            ...values,
            StateId: values.State,
            State: selectedState?.State || '',
          };

          if (isEdit) {
            onEdit(formattedData);
          } else {
            onUploadMinimumWages(formattedData);
          }
        }}
      >
        {({ setFieldValue, values, setFieldError, setFieldTouched, isValid, dirty }) => (
          <Form>
            <div className="grid grid-cols-3 gap-4 mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                  <span className="text-red-500">*</span></label>
                <Field
                  as="select"
                  name="State"
                  disabled={isEdit}
                  onChange={(e) => {
                    const selected = e.target.value;
                    setFieldValue('State', selected);
                    onSetState(selected)
                  }}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEdit ? 'bg-gray-200' : ''}`}
                >
                  <option value="">Select State</option>
                  {Array.isArray(states) &&
                    [...states].sort((a, b) => a.State.localeCompare(b.State)).map(state => (
                      <option key={state.StateId} value={state.StateId}>{state.State}</option>
                    ))}
                </Field>
                <ErrorMessage name="State" component="div" className="text-sm text-red-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year <span className="text-red-500">*</span></label>
                <Field
                  as="select"
                  name="year"
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${data ? 'bg-gray-200 pointer-events-none' : 'bg-white'
                    }`}
                >
                  <option value="" disabled={!values.State || !isEdit}>Select Year</option>
                  {yearsOption.map(year => <option key={year} value={year}>{year}</option>)}
                </Field>
                <ErrorMessage name="year" component="div" className="text-sm text-red-600" />
              </div>
              <IndustryAutocomplete
                name="industryType"
                label="Industry"
                industryTypes={values.State ? industryTypes : []}
                disabled={!!data}
              />
            </div>
            <>
              {(!industryTypes.length && values.State) ?
                <div style={{ height: 'calc(100% - 100px)' }} className="w-full p-4 flex flex-col justify-center gap-4 items-center mt-4">
                  <FaCircleInfo className="text-3xl text-gray-600" />
                  <p className="text-center text-gray-700">You haven’t added any Industry Types yet.</p>
                  <Link to="/industry-type-management" className="w-fit">
                    <div className="p-2 bg-primary-color text-white flex gap-2 items-center rounded-md w-fit hover:opacity-90 transition">
                      <FaPlus />
                      Create Industry Type
                    </div>
                  </Link>
                </div> :
                <>
                  <div className="mt-6 space-y-6">
                    <FileUploadField
                      name="fileUrl"
                      label="Upload File"
                      values={values}
                      setFieldTouched={setFieldTouched}
                      setFieldError={setFieldError}
                      setFieldValue={setFieldValue}
                      handleOnUploadFile={handleOnUploadFile}
                    />
                    <div className='grid grid-cols-2 gap-4 mt-4'>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notification Link</label>
                        <Field
                          type="url"
                          name="referenceLink"

                          placeholder="https://example.com/notification"
                          className={`w-full border rounded px-3 py-2 `}
                        />
                        <ErrorMessage name="referenceLink" component="div" className="text-sm text-red-600" />
                      </div>

                      {/* Effective From */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Effective From <span className="text-red-500">*</span></label>
                        <Field
                          type="date"
                          name="EffectiveFrom"
                          className={`w-full border rounded px-3 py-2 `}
                        />
                        <ErrorMessage name="EffectiveFrom" component="div" className="text-sm text-red-600" />
                      </div>

                      {/* Effective To */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Effective To</label>
                        <Field
                          type="date"
                          name="EffectiveTo"
                          min={values.EffectiveFrom || new Date().toISOString().split('T')[0]}
                          className={`w-full border rounded px-3 py-2 `}
                        />
                        <ErrorMessage name="EffectiveTo" component="div" className="text-sm text-red-600" />
                      </div>

                      {/* Notification No */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Notification No <span className="text-red-500">*</span></label>
                        <Field
                          type="text"
                          name="NotificationNo"
                          placeholder="Enter notification number"
                          className={`w-full border rounded px-3 py-2 `}
                        />
                        <ErrorMessage name="NotificationNo" component="div" className="text-sm text-red-600" />
                      </div>

                      {/* Notified On */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Notified On</label>
                        <Field
                          type="date"
                          name="NotifiedOn"
                          className={`w-full border rounded px-3 py-2 `}
                        />
                        <ErrorMessage name="NotifiedOn" component="div" className="text-sm text-red-600" />
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-6 w-full flex justify-end">
                    <button
                      disabled={
                        (!isEdit && !isValid) ||
                        (isEdit && !dirty)
                      }
                      type="submit"
                      className={`bg-green-400 text-white px-6 py-2 rounded hover:bg-green-800 transition flex gap-2 items-center
                  ${(!isEdit && !isValid) || (isEdit && !dirty) ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                    >
                      {/* <FiUploadCloud className="text-white text-xl" /> */}
                      {isEdit ? 'Update' : 'Upload'}
                    </button>
                  </div>
                </>
              }
            </>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default UploadMinimumWagesForm;
